// ============================================
// MoltiVerse v3 - Global Error Boundary
// ============================================
// Prevents crashes from propagating and provides recovery UI
// Now with centralized error tracking

import { Component, ErrorInfo, ReactNode } from 'react';
import { captureReactError, captureMessage } from '@/lib/error-tracking';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    
    // Send to centralized error tracking (Sentry if configured)
    captureReactError(error, errorInfo, 'ErrorBoundary');
    
    // Also log to console for development
    console.error('Error caught by boundary:', error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="fixed inset-0 bg-[#030306] flex items-center justify-center p-8">
          <div className="max-w-lg w-full">
            {/* Error container */}
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-8 text-center">
              {/* Icon */}
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
                <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>

              {/* Title */}
              <h2 className="text-xl font-display font-bold text-white mb-2">
                System Malfunction Detected
              </h2>

              {/* Message */}
              <p className="text-white/60 mb-6">
                The MoltiVerse Oracle has encountered an unexpected anomaly. 
                Our neural networks are attempting to self-repair.
              </p>

              {/* Error details (dev only) */}
              {process.env.NODE_ENV !== 'production' && this.state.error && (
                <div className="bg-black/50 rounded p-4 mb-6 text-left">
                  <p className="font-mono text-xs text-red-400 mb-2">
                    {this.state.error.name}: {this.state.error.message}
                  </p>
                  {this.state.errorInfo && (
                    <pre className="font-mono text-[10px] text-white/40 overflow-auto max-h-32">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-4 justify-center">
                <button
                  onClick={this.handleReset}
                  className="px-6 py-2.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-white font-medium transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={this.handleReload}
                  className="px-6 py-2.5 bg-cyber-glow/20 hover:bg-cyber-glow/30 border border-cyber-glow/40 rounded-lg text-cyber-glow font-medium transition-colors"
                >
                  Reload Oracle
                </button>
              </div>
            </div>

            {/* Footer */}
            <p className="text-center text-white/30 text-xs mt-6 font-mono">
              ERROR_BOUNDARY_v3.0 :: QUANTUM_RECOVERY_PROTOCOL_ACTIVE
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Specialized boundary for the 3D scene
export function SceneErrorFallback(): JSX.Element {
  return (
    <div className="w-full h-full flex items-center justify-center bg-[#030306]">
      <div className="text-center p-8">
        <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-amber-500/20 flex items-center justify-center">
          <svg className="w-6 h-6 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <h3 className="text-lg font-display font-bold text-white mb-2">
          3D Engine Offline
        </h3>
        <p className="text-white/50 text-sm max-w-xs">
          The galaxy visualization could not be rendered. 
          Try refreshing the page.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded text-white/70 text-sm transition-colors"
        >
          Refresh
        </button>
      </div>
    </div>
  );
}
